import { moveToFulfilled, moveToPending, moveToRejected, update } from '@ms/yammer-data-support';
import { Reducer } from 'redux';

import {
  AddToDirectAudienceFulfilledAction,
  RemoveFromDirectAudienceFulfilledAction,
  UserDirectAudienceAction,
  UserDirectAudienceLoadMoreFulfilledAction,
} from '../../domains/leadersManagement/actions.types';

import { UserDirectAudiences } from './types';

export type UserDirectAudienceActions =
  | UserDirectAudienceAction
  | AddToDirectAudienceFulfilledAction
  | RemoveFromDirectAudienceFulfilledAction
  | UserDirectAudienceLoadMoreFulfilledAction;

const initialState: UserDirectAudiences = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const stateWithAddToDirectAudienceFulfilled = (
  state: UserDirectAudiences,
  { userId, directAudienceMembers }: AddToDirectAudienceFulfilledAction['payload']
): UserDirectAudiences => {
  const fulfilledUserDirectAudience = state.fulfilled.byId[userId];

  if (fulfilledUserDirectAudience) {
    const existingDirectAudienceMemberIds = new Set(fulfilledUserDirectAudience.directAudiences.map(({ id }) => id));

    const newUniqueDirectAudienceMembers = directAudienceMembers.filter(
      ({ id }) => !existingDirectAudienceMemberIds.has(id)
    );

    const updatedDirectAudience = {
      directAudiences: [...fulfilledUserDirectAudience.directAudiences, ...newUniqueDirectAudienceMembers],
    };

    return update(state, userId, updatedDirectAudience);
  }

  return state;
};

const stateWithRemoveFromDirectAudienceFulfilled = (
  state: UserDirectAudiences,
  { userId, audienceMemberIds }: RemoveFromDirectAudienceFulfilledAction['payload']
): UserDirectAudiences => {
  const fulfilledUserDirectAudience = state.fulfilled.byId[userId];

  if (fulfilledUserDirectAudience) {
    const directAudiences = fulfilledUserDirectAudience.directAudiences.filter(
      ({ id }) => audienceMemberIds.indexOf(id) < 0
    );
    const updatedDirectAudience = {
      directAudiences,
    };

    return update(state, userId, updatedDirectAudience);
  }

  return state;
};

const appendMoreDirectAudience = (
  state: UserDirectAudiences,
  { userId, entities }: UserDirectAudienceLoadMoreFulfilledAction['payload']
): UserDirectAudiences => {
  const oldDirectAudience = state.fulfilled.byId[userId];
  const newDirectAudience = entities.userDirectAudience[userId];

  if (oldDirectAudience) {
    return update(state, userId, {
      ...newDirectAudience,
      directAudiences: [...oldDirectAudience.directAudiences, ...newDirectAudience.directAudiences],
    });
  }

  return moveToFulfilled(state, entities.userDirectAudience);
};

const reducer: Reducer<UserDirectAudiences, UserDirectAudienceActions> = (state = initialState, action) => {
  switch (action.type) {
    case 'USER_DIRECT_AUDIENCE.LOAD_PENDING':
      return moveToPending(state, [action.payload.userId]);
    case 'USER_DIRECT_AUDIENCE.LOAD_REJECTED':
      return moveToRejected(state, [action.payload.userId], action.payload.error);
    case 'USER_DIRECT_AUDIENCE.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.userDirectAudience);
    case 'ADD_TO_DIRECT_AUDIENCE.FULFILLED':
      return stateWithAddToDirectAudienceFulfilled(state, action.payload);
    case 'REMOVE_FROM_DIRECT_AUDIENCE.FULFILLED':
      return stateWithRemoveFromDirectAudienceFulfilled(state, action.payload);
    case 'USER_DIRECT_AUDIENCE.LOAD_MORE_FULFILLED':
      return appendMoreDirectAudience(state, action.payload);
    default:
      return state;
  }
};

export default reducer;
